// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { isEmpty } from 'lodash'
import { Edit } from 'grommet-icons'

import { Anchor, Button, Typography } from '../../../components'
import VisibilityWrapper from '../../../commoncomponents/visibility-wrapper/VisibilityWrapper'
import { isMSP, getCustomerAccount } from '../../../utils/feature-flag-utils'

import {
  glcpServiceID,
  MSP_SCOPE_ASSIGNMENT_TYPE,
  MSP_ALL_CUSTOMER_WORKSPACES_GRN_SUFFIX,
  SCOPE_TYPES,
  TENANT_WORKSPACE_SCOPES,
  WORKSPACE_SCOPES,
  USER_SCHEMAS
} from './constants'

const getNameAndType = (scopeResource, scopesData) => {
  let name = ''
  const scopeInstance = scopesData?.find(
    (scopeData) =>
      scopeData?.bundledData?.application_instance_id ===
      scopeResource?.application_instance_id
  )
  const matchedChild = scopeInstance?.bundledData?.children?.find((child) => {
    const foundPf = child?.predefined_filters?.find((pf) => {
      return pf.slug === scopeResource.slug
    })
    if (foundPf) {
      name = foundPf?.name
      return true
    }
    const foundSri = child?.scope_resource_instances?.find((sri) => {
      return sri.slug === scopeResource.slug
    })
    if (foundSri) {
      name = foundSri?.name
      return true
    }
    return false
  })
  const matchedType = scopeInstance?.bundledData?.scope_resources?.find(
    (sr) => sr?.slug === matchedChild?.scope
  )
  if (name === '') {
    return {}
  }
  return {
    name,
    scope_type_name: matchedType?.name || '',
    scope_type_slug: matchedType?.slug,
    type: matchedType?.slug
  }
}

const getUpdatedScopeMapFromScopesResponse = (
  onModalScopesMap,
  scopeResources,
  scopeTypeSlug,
  scopeTypeName
) => {
  onModalScopesMap.forEach((value, key) => {
    const slug = key.split('~')[1]
    if (
      !value?.name ||
      value?.name === 'generated_via_migration' ||
      value?.name === slug ||
      !value?.scope_type_name ||
      value?.scope_type_name === ''
    ) {
      const matchedScope = scopeResources.find((val) => val.slug === slug)
      if (matchedScope)
        onModalScopesMap.set(key, {
          ...value,
          ...{
            name: matchedScope?.name,
            scope_type_slug: scopeTypeSlug,
            scope_type_name: scopeTypeName,
            type: scopeTypeSlug
          }
        })
    }
  })
  return onModalScopesMap
}

const getSelectedScopeMap = (scopeResources = [], scopesData) => {
  const scopeMap = new Map()
  scopeResources.forEach((scopeResource) => {
    let foundScopeItem = {}
    const { application_instance_id: applicationInstanceId, slug } =
      scopeResource
    if (
      !scopeResource.name ||
      scopeResource?.name === 'generated_via_migration' ||
      scopeResource?.name === slug ||
      !scopeResource?.scope_type_name ||
      scopeResource?.scope_type_name === '' ||
      (scopeResource?.name === '' && scopesData)
    ) {
      foundScopeItem = getNameAndType(scopeResource, scopesData)
    }
    const key = `${applicationInstanceId}~${slug}`
    scopeMap.set(key, { ...scopeResource, ...foundScopeItem })
  })
  return scopeMap
}

const mapRoleAssignments = (roleArray, scopeArray, roleAssignmentList) => {
  const roleAssignments = []
  roleArray?.forEach((role) => {
    scopeArray?.forEach((scope) => {
      if (
        !roleAssignmentList?.find(
          (assignment) =>
            assignment.role_id === role.role_id && assignment.scope === scope
        )
      ) {
        const primaryKey = role?.role_id?.concat(scope)
        roleAssignments?.push({
          primaryKey,
          role_id: role?.role_id,
          role_name: role?.role_name,
          role_display_name: role?.role_display_name,
          role_grn: role?.role_grn,
          service_id: role?.service_id,
          service_name: role?.service_name,
          custom_role_service_id: role?.custom_role_service_id,
          custom_role_service_name: role?.custom_role_service_name,
          managedBy: role?.managedBy,
          scope
        })
      }
    })
  })
  return roleAssignments
}

const mapMultiscopeRoleAssignment = (roleArray, scopeArray) => {
  const primaryKey = roleArray[0]?.role_id
  const roleAssignment = {
    primaryKey,
    role_id: roleArray[0]?.role_id,
    role_name: roleArray[0]?.role_name,
    role_display_name: roleArray[0]?.role_display_name,
    role_grn: roleArray[0]?.role_grn,
    service_id: roleArray[0]?.service_id,
    service_name: roleArray[0]?.service_name,
    custom_role_service_id: roleArray[0]?.custom_role_service_id,
    custom_role_service_name: roleArray[0]?.custom_role_service_name,
    managedBy: roleArray[0]?.managedBy,
    scopes: scopeArray
  }
  return roleAssignment
}

const replaceEndWith = (original, target, replacement) => {
  if (!original) return ''
  const lastIndex = original.lastIndexOf(target)
  return lastIndex === -1
    ? original
    : original.substring(0, lastIndex) + replacement
}

const getResourceTypeName = (resType) => {
  // name attribute from UID, maps to BE displayName or '' if no display name was provided
  // display name is currently optional; use slug if not provided
  return resType?.name || replaceEndWith(resType?.slug, '/{uuid}', '')
}

const getAllInstancesOfResourceTypeSlug = (resourceType) => {
  return replaceEndWith(resourceType?.value?.slug, '{uuid}', '*')
}

const filterScopelessResourceTypes = (resourceTypes) => {
  const resTypeOptions = []
  resourceTypes?.forEach((resType) => {
    // filter scopeless resource types, ie ones which don't end with '{uuid}'
    if (resType?.slug?.endsWith('{uuid}'))
      resTypeOptions.push({
        label: getResourceTypeName(resType),
        value: resType
      })
  })
  return resTypeOptions
}

const getMultiScopesInfo = (datum, t, type, navigate, isAuthzV2London) => {
  let scopesInfo = ''
  if (datum?.scopes) {
    scopesInfo = datum?.scopes?.map((scope, idx) => {
      let scopeInfo
      if (
        scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_WORKSPACE &&
        type === SCOPE_TYPES.MSP
      ) {
        scopeInfo = (
          <div key={scope?.scopeId || idx}>
            <Typography type="text">
              {t('iam:assign_roles_side_panel.access_all_workspace_label', {
                workspace: t('common:business_object.wkspc')
              })}
            </Typography>
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </div>
        )
      } else if (
        scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_SCOPE_GROUP &&
        type === SCOPE_TYPES.MSP
      ) {
        scopeInfo = navigate ? (
          <div key={scope?.scopeId || idx}>
            <Anchor
              href="#"
              label={scope?.scopeName}
              testId="assignment-anchor"
              onClick={(event) => {
                event.preventDefault()
                navigate(
                  `/manage-account/identity/scopegroups/${scope?.scopeId}`
                )
              }}
            />
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </div>
        ) : (
          <>
            <Typography type="text" emphasis>
              {scope?.scopeName}
            </Typography>
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </>
        )
      } else if (
        scope?.scopeType ===
          MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_ALL_CUSTOMERS &&
        type === SCOPE_TYPES.CUSTOMER
      ) {
        scopeInfo = (
          <>
            <Typography type="text">
              {t('iam:msp.msp_scope_all_customers')}
            </Typography>
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </>
        )
      } else if (
        scope?.scopeType ===
          MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_WORKSPACE_GROUP &&
        type === SCOPE_TYPES.CUSTOMER
      ) {
        scopeInfo = navigate ? (
          <div key={scope?.scopeId || idx}>
            <Anchor
              href="#"
              label={scope?.scopeName}
              testId="assignment-anchor"
              onClick={(event) => {
                event.preventDefault()
                navigate(
                  `/manage-account/identity/workspace-groups/${scope?.scopeId}`
                )
              }}
            />
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </div>
        ) : (
          <>
            <Typography type="text" emphasis>
              {scope?.scopeName}
            </Typography>
            {!isAuthzV2London && (
              <Typography type="text">{scope?.scope}</Typography>
            )}
          </>
        )
      }
      return scopeInfo
    })
  }
  return scopesInfo?.every((scopeInfo) => scopeInfo === undefined)
    ? t('iam:msp.msp_scope_none')
    : scopesInfo
}

const getScopeInfo = (datum, navigate, t) => {
  let scopeInfo = ''
  if (datum?.scopeGroupName) {
    scopeInfo = (
      <>
        <Anchor
          href="#"
          label={datum?.scopeGroupName}
          testId="assignment-anchor"
          onClick={(event) => {
            event.preventDefault()
            navigate(
              `/manage-account/identity/scopegroups/${datum?.scopeGroupId}`
            )
          }}
        />
        <Typography type="text">{datum?.scope}</Typography>
      </>
    )
  } else if (datum?.scope) {
    const entireWorkspaceGrn = `grn:glp/workspaces/${
      getCustomerAccount()?.platform_customer_id
    }`
    if (datum?.scope === entireWorkspaceGrn) {
      scopeInfo = (
        <>
          <Typography type="text" emphasis>
            {t('iam:assign_roles_side_panel.access_all_workspace_label', {
              workspace: t('common:business_object.wkspc')
            })}
          </Typography>
          <Typography type="text">{datum?.scope}</Typography>
        </>
      )
    } else {
      scopeInfo = datum?.scope
    }
  }
  return scopeInfo
}

const authzSAMLSSOMode = [
  'AUTHORIZATION',
  'AUTHORIZATION_MSP_MANAGED',
  'AUTHORIZATION_MSP_MANAGED_TENANT_ONLY'
]

const isSAMLAuthzUser = (userInfo) => {
  return (
    authzSAMLSSOMode?.includes(userInfo?.ssoMode) ||
    authzSAMLSSOMode.includes(userInfo?.sso_mode)
  )
}

const getSubjectName = (subject, type) => {
  let name
  // use user's displayName or userName if not found
  if (type === 'USER') name = subject?.displayName || subject?.userName
  else if (type === 'GROUP') name = subject?.displayName
  else name = subject?.name // APIClient
  return name
}

const getMultiScopeColumns = (isAuthzV2London, t, navigate) => {
  const v2LondonScope = [
    {
      header: isMSP()
        ? t('iam:msp.msp_workspace_scope')
        : t('iam:role_assignment.scope_capitalized'),
      property: 'meta.scope',
      sortable: false,
      render: (datum) => {
        return getMultiScopesInfo(
          datum,
          t,
          SCOPE_TYPES.MSP,
          navigate,
          isAuthzV2London
        )
      }
    },
    ...(isMSP()
      ? [
          {
            header: t('iam:msp.customer_workspace_scope'),
            property: 'meta.customerscope',
            sortable: false,
            render: (datum) => {
              return getMultiScopesInfo(
                datum,
                t,
                SCOPE_TYPES.CUSTOMER,
                navigate,
                isAuthzV2London
              )
            }
          }
        ]
      : [])
  ]
  return isAuthzV2London
    ? v2LondonScope
    : [
        {
          header: t('iam:role_assignment.scope_capitalized'),
          property: 'meta.scope',
          sortable: false,
          render: (datum) => {
            return getScopeInfo(datum, navigate, t)
          }
        }
      ]
}

const isCCSRole = (roleAssignment) => {
  return (
    roleAssignment?.service_id === glcpServiceID ||
    roleAssignment?.custom_role_service_id === glcpServiceID
  )
}

const showRAEditBtn = (roleAssignment) => {
  return (
    // Edit btn is hidden for CCS role assignment for V2 standard and customer workspace
    // In this case ENTIRE scope option is default and only option for CCS role assignment
    (isMSP() || !isCCSRole(roleAssignment)) &&
    (roleAssignment?.source === 'Direct' ||
      roleAssignment?.source === undefined)
  )
}

const getEditRABtnColumn = (isV2London, handleOnEditRA) => {
  return isV2London
    ? [
        {
          header: '',
          property: 'meta.edit',
          sortable: false,
          render: (datum) => {
            if (showRAEditBtn(datum)) {
              return (
                <VisibilityWrapper
                  rbac={{
                    permission: 'ccs.authorization.edit'
                  }}
                >
                  <Button
                    icon={<Edit />}
                    onClick={() => handleOnEditRA(datum)}
                    data-testid="role-assignments-table-data-edit"
                  />
                </VisibilityWrapper>
              )
            }
            return null
          }
        }
      ]
    : []
}

const getProviderNameFromScopeGRN = (grn) => {
  // Remove beginning of grn so that the grn looks like providers/:provider_name/resource_type/resource_id
  let index = grn?.indexOf('providers')
  let providerName = grn?.substring(index)

  // Remove providers prefix so that the grn looks like :provider_name/resource_type/resource_id
  index = providerName?.indexOf('/')
  providerName = providerName?.substring(index + 1)

  // Remove grn ending so that the grn looks like :provider_name
  index = providerName?.indexOf('/')
  providerName = providerName?.substring(0, index)

  return providerName
}

const validateScopeSelection = (
  state,
  t,
  setScopeError,
  setScopeGroupInstanceError,
  setWkspcGroupScopeError,
  setWkspcGroupInstanceError,
  setMspScopeError
) => {
  let result = false
  const isScopeSelected = state?.scope !== ''
  if (!isScopeSelected) {
    setScopeError(
      isMSP()
        ? t('iam:msp.msp_workspace_scope_error', {
            workspace: t('common:business_object.wkspc')
          })
        : t('iam:assign_roles_side_panel.select_scope_error_msg')
    )
  }
  let isCustomerWkspcScopeSelected
  let isWkspcGroupSelected
  if (isMSP()) {
    isCustomerWkspcScopeSelected = state?.customerWorkspaceScope !== ''
    if (!isCustomerWkspcScopeSelected) {
      setWkspcGroupScopeError(
        t('iam:msp.customer_workspace_scope_error', {
          workspace: t('common:business_object.wkspc')
        })
      )
    }

    isWkspcGroupSelected =
      state?.customerWorkspaceScope ===
        TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS &&
      !isEmpty(state?.workspaceGroupInstances) &&
      !state?.workspaceGroupInstances?.some(
        (scope) =>
          scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_ALL_CUSTOMERS
      )
    if (!isWkspcGroupSelected) {
      setWkspcGroupInstanceError(
        t('iam:msp.select_workspace_groups_error', {
          wkspcGroup: t('common:business_object.wkspc_group')
        })
      )
    }
  }
  const isScopeGroupSelected =
    state?.scope === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS &&
    !isEmpty(state?.scopeGroupInstances) &&
    !state?.scopeGroupInstances?.some(
      (scope) => scope?.scopeType === MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_WORKSPACE
    )
  if (!isScopeGroupSelected) {
    setScopeGroupInstanceError(
      t('iam:assign_roles_side_panel.select_scope_groups_error_msg', {
        scopeGroup: t('common:business_object.scope_group')
      })
    )
  }

  // true if all below true:
  // - workspace scope option selected
  // - workspace scope option not specific scope OR
  //   workspace scope option is specific AND some scope groups selected
  // - Not MSP - don't check for customer scope selection
  // - MSP
  //   + customer workspace scope option selected
  //   + customer workspace scope option not specific scope OR
  //     customer workspace scope option is specific AND some workspace groups selected
  result =
    isScopeSelected &&
    (state?.scope !== WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS ||
      isScopeGroupSelected) &&
    (!isMSP() ||
      (isCustomerWkspcScopeSelected &&
        (state?.customerWorkspaceScope !==
          TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS ||
          isWkspcGroupSelected)))

  if (result) {
    if (
      state?.customerWorkspaceScope === TENANT_WORKSPACE_SCOPES?.NONE &&
      state?.scope === WORKSPACE_SCOPES?.NONE
    ) {
      result = false
      setMspScopeError(t('iam:msp.select_scope_validation_msg'))
    }
  }

  if (result) {
    if (
      state?.customerWorkspaceScope === TENANT_WORKSPACE_SCOPES?.NONE &&
      state?.scope === WORKSPACE_SCOPES?.NONE
    ) {
      result = false
      setMspScopeError(t('iam:msp.select_scope_validation_msg'))
    }
  }
  return result
}

const getScopesFromStateInfo = (state, workSpaceGrn, isNewSubject = false) => {
  const scopes = []
  if (state?.scope === WORKSPACE_SCOPES?.ENTIRE) {
    scopes?.push(
      isNewSubject
        ? {
            scope: workSpaceGrn,
            scopeType: MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_WORKSPACE
          }
        : workSpaceGrn
    )
  } else if (state?.resource === 'ALL_RESOURCES_OPTION') {
    const allInstancesOfResourceTypeSlug = getAllInstancesOfResourceTypeSlug(
      state?.resourceType
    )
    const resourceTypeGRN = `${workSpaceGrn}/regions/${state?.region?.value}/providers/${allInstancesOfResourceTypeSlug}`
    scopes?.push(resourceTypeGRN)
  } else if (state?.scope === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS) {
    state?.scopeGroupInstances?.forEach((instance) => {
      const scopeGrn = instance?.grn || instance?.scope
      scopes?.push(
        isNewSubject
          ? {
              scope: scopeGrn,
              scopeName: instance?.name,
              scopeType: MSP_SCOPE_ASSIGNMENT_TYPE?.MSP_SCOPE_GROUP
            }
          : scopeGrn
      )
    })
  }
  if (state?.customerWorkspaceScope === TENANT_WORKSPACE_SCOPES?.ALL) {
    const allTenantsGrn = `${workSpaceGrn}/${MSP_ALL_CUSTOMER_WORKSPACES_GRN_SUFFIX}`
    scopes?.push(
      isNewSubject
        ? {
            scope: allTenantsGrn,
            scopeType: MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_ALL_CUSTOMERS
          }
        : allTenantsGrn
    )
  } else if (
    state?.customerWorkspaceScope ===
    TENANT_WORKSPACE_SCOPES?.SPECIFIC_WORKSPACE_GROUPS
  ) {
    state?.workspaceGroupInstances?.forEach((instance) => {
      const scopeGrn = instance?.grn || instance?.scope
      scopes?.push(
        isNewSubject
          ? {
              scope: scopeGrn,
              scopeName: instance?.displayName,
              scopeType: MSP_SCOPE_ASSIGNMENT_TYPE?.CUSTOMER_WORKSPACE_GROUP
            }
          : scopeGrn
      )
    })
  }
  return scopes
}

const isExternalUser = (userInfo) => {
  const userSource =
    userInfo?.[USER_SCHEMAS?.EXTENSIONS]?.source || userInfo?.source
  return userSource === 'External'
}

// When a user chooses a msp one role app selection, this checks to see which scopes the user's existing one role app
// role assignment currently has. If both msp and customer scopes exist, we display the one role app warning notification
// right away since we can only have one msp scope and one customer scope for one role apps. If it's one and not the other,
// the user can choose a second one role app and assign the other scope that's not already assigned.
const checkIfMSPScopesAreOneRoleApp = (
  userOneRoleAppRoleAssignment = {},
  setHasMSPOneRoleAppScope = () => {},
  setHasCustomerOneRoleAppScope = () => {},
  setHasConflictingMSPOneRoleAppRA = () => {}
) => {
  // Check to see if existing one role app role assignment has an MSP scope for both MSP and customer scope.
  const hasMSPOneRoleAppScope = userOneRoleAppRoleAssignment?.scopes?.some(
    (scope) => scope?.scopeType?.includes(SCOPE_TYPES.MSP)
  )
  setHasMSPOneRoleAppScope(hasMSPOneRoleAppScope)

  const hasCustomerOneRoleAppScope = userOneRoleAppRoleAssignment?.scopes?.some(
    (scope) => scope?.scopeType?.includes(SCOPE_TYPES.CUSTOMER)
  )
  setHasCustomerOneRoleAppScope(hasCustomerOneRoleAppScope)

  if (hasMSPOneRoleAppScope && hasCustomerOneRoleAppScope) {
    setHasConflictingMSPOneRoleAppRA(true)
  }
}

export {
  getSelectedScopeMap,
  getUpdatedScopeMapFromScopesResponse,
  mapRoleAssignments,
  mapMultiscopeRoleAssignment,
  getResourceTypeName,
  filterScopelessResourceTypes,
  getAllInstancesOfResourceTypeSlug,
  getScopeInfo,
  getSubjectName,
  getMultiScopeColumns,
  getMultiScopesInfo,
  isSAMLAuthzUser,
  isCCSRole,
  getEditRABtnColumn,
  validateScopeSelection,
  getScopesFromStateInfo,
  isExternalUser,
  getProviderNameFromScopeGRN,
  checkIfMSPScopesAreOneRoleApp
}
